import { Accordion, Container, GridContainer } from "@fluidattacks/design";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { formatAdvisoryUrls } from "./formatters/advisory-urls";
import { formatDependencyName } from "./formatters/dependency-name";
import { formatReachability } from "./formatters/reachability";
import { formatVersionStatus } from "./formatters/version-status";

import { formatDependencyType } from "../../../formatters/dependency-type";
import { Detail } from "../../detail";
import { Value } from "../../value";
import type { GetVulnAdditionalInfoQuery } from "gql/graphql";
import { usePackageQuery } from "pages/group/supply-chain/package-details/hooks";

const PackageDetailsSection = ({
  advisories,
  groupName,
  rootId,
}: Readonly<{
  advisories: GetVulnAdditionalInfoQuery["vulnerability"]["advisories"];
  groupName: string;
  rootId: string;
}>): JSX.Element | undefined => {
  const { t } = useTranslation();

  const { pkg } = usePackageQuery(
    groupName,
    advisories?.package ?? "",
    rootId,
    advisories?.vulnerableVersion ?? "",
  );

  const advisoryAdditionalInfo = pkg?.advisories?.find(
    (adv): boolean => adv.id === advisories?.cve?.[0],
  );

  const healthMetadata = pkg?.healthMetadata ?? undefined;

  return (
    <Accordion
      items={[
        {
          description: (
            <Container>
              <GridContainer
                gap={0.75}
                justify={"space-between"}
                lg={2}
                md={2}
                sm={1}
                xl={2}
              >
                <Detail
                  field={formatDependencyName(advisories)}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.dependency",
                  )}
                />
                <Detail
                  field={formatDependencyType(advisories?.dependencyType)}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.dependencyType",
                  )}
                />
                <Detail
                  field={<Value value={advisories?.cve?.[0]} />}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.advisoryId",
                  )}
                />
                <Detail
                  field={
                    <Value
                      value={
                        _.isNil(advisories?.epss)
                          ? undefined
                          : `${advisories.epss}%`
                      }
                    />
                  }
                  label={t("searchFindings.tabVuln.vulnTable.advisories.epss")}
                />
                <Detail
                  field={
                    <Value
                      value={
                        _.isNil(advisories?.scope)
                          ? undefined
                          : _.capitalize(advisories.scope)
                      }
                    />
                  }
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.status",
                  )}
                />
                <Detail
                  field={formatReachability(advisories?.reachabilityStatus)}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.reachability",
                  )}
                />
                <Detail
                  field={formatVersionStatus(
                    pkg?.outdated,
                    advisories?.vulnerableVersion,
                    healthMetadata?.latestVersion,
                  )}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.versionStatus",
                  )}
                />
                <Detail
                  field={<Value value={advisories?.vulnerableVersion} />}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.affectedVersion",
                  )}
                />
                <Detail
                  field={
                    <Value
                      value={
                        _.isNil(advisoryAdditionalInfo?.cpes)
                          ? undefined
                          : advisoryAdditionalInfo.cpes.join(", ")
                      }
                    />
                  }
                  label={t("searchFindings.tabVuln.vulnTable.advisories.cpes")}
                />
                <Detail
                  field={<Value value={advisoryAdditionalInfo?.namespace} />}
                  label={t(
                    "searchFindings.tabVuln.vulnTable.advisories.namespace",
                  )}
                />
              </GridContainer>
              <Detail
                field={formatAdvisoryUrls(advisoryAdditionalInfo?.urls)}
                label={`${t(
                  "searchFindings.tabVuln.vulnTable.advisories.advisoryUrls",
                )} (${advisoryAdditionalInfo?.urls.length ?? 0})`}
              />
            </Container>
          ),
          expanded: true,
          title: t("searchFindings.tabVuln.vulnTable.packageDetails"),
        },
      ]}
    />
  );
};

export { PackageDetailsSection };
